/**
 * Client helpers for Payroll Dashboard — structure/readiness from existing props only.
 * Processed cost/cash figures come from the analytics API (register summary).
 */

/** Display amounts in Lakhs (L) / Crores (Cr) for charts & cards. */
export function formatInrLakhs(n, opts = {}) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  const decimals = opts.decimals != null ? opts.decimals : abs >= 10000000 ? 2 : abs >= 1000 ? 2 : 0;
  if (abs === 0) return opts.showZero === false ? "—" : "0 L";
  if (abs >= 10000000) return `${sign}${(abs / 10000000).toFixed(decimals)} Cr`;
  if (abs >= 1000 || opts.forceLakhs) return `${sign}${(abs / 100000).toFixed(decimals)} L`;
  return `${sign}${Math.round(abs).toLocaleString("en-IN")}`;
}

export function formatInrCompact(n, opts = {}) {
  if (opts.lakhs !== false) return formatInrLakhs(n, opts);
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  if (v === 0) return opts.showZero === false ? "—" : "₹0";
  if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
  if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
}

export function formatInrFull(n, opts = {}) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  if (v === 0 && opts.showZero === false) return "—";
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
}

/** jsPDF Helvetica-safe money (no rupee glyph / special chars). */
export function formatInrPdf(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "-";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs === 0) return "Rs 0";
  if (abs >= 10000000) return `${sign}Rs ${(abs / 10000000).toFixed(2)} Cr`;
  if (abs >= 100000) return `${sign}Rs ${(abs / 100000).toFixed(2)} L`;
  return `${sign}Rs ${Math.round(abs).toLocaleString("en-IN")}`;
}

/** Auto font size for chart data labels based on available width. */
export function autoLabelFontSize(text, maxWidthPx, min = 7, max = 11) {
  const t = String(text || "");
  const approxChar = maxWidthPx / Math.max(t.length, 1);
  return Math.max(min, Math.min(max, Math.floor(approxChar * 1.35)));
}

/** Prefer register totals when processed; else structure/readiness for fixed-only cards. */
export function resolvePulseDisplay(analytics, readiness) {
  const status = String(analytics?.status || "").toLowerCase();
  const totals = analytics?.totals || {};
  const processed =
    status === "processed" ||
    status === "finalised" ||
    status === "finalized" ||
    status === "aggregated";
  const hasEarned =
    processed &&
    (Number(totals.totalCost) > 0 ||
      Number(totals.totalCashFlow) > 0 ||
      Number(analytics?.headcount) > 0);

  const fixedCost =
    Number(totals.fixedCost) > 0
      ? Number(totals.fixedCost)
      : Number(readiness?.structureFixedCost) || 0;
  const avgFixed =
    Number(totals.avgFixedCost) > 0
      ? Number(totals.avgFixedCost)
      : Number(readiness?.avgStructureFixed) || 0;

  return {
    status,
    processed: hasEarned,
    headcount: hasEarned ? Number(analytics.headcount) || 0 : Number(readiness?.total) || 0,
    headcountSource: hasEarned ? "register" : "scope",
    totalCost: hasEarned ? Number(totals.totalCost) || 0 : null,
    totalCashFlow: hasEarned ? Number(totals.totalCashFlow) || 0 : null,
    netPay: hasEarned ? Number(totals.netPay) || 0 : null,
    grossPay: hasEarned ? Number(totals.grossPay) || 0 : null,
    avgEarnedCost: hasEarned ? Number(totals.avgEarnedCost) || 0 : null,
    fixedCost,
    avgFixed,
    fixedSource: Number(totals.fixedCost) > 0 ? "register" : "structure",
    totals,
  };
}

export function statusBadgeLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "finalised" || s === "finalized") return "Finalised";
  if (s === "processed") return "Processed";
  if (s === "aggregated") return "Aggregated";
  if (s === "not_processed") return "Not processed";
  if (s === "no_run") return "No payroll run";
  return status || "-";
}

/** Build rows for a pulse card drill-down (matches UI cards). */
export function buildCardDetailPayload(cardKey, { analytics, readiness, pulse, trend = [] }) {
  const p = pulse || resolvePulseDisplay(analytics, readiness);
  const t = analytics?.totals || {};
  const commonMeta = {
    periodLabel: analytics?.periodLabel || "",
    status: statusBadgeLabel(analytics?.status),
    generatedAt: new Date().toISOString(),
  };

  const trendRows = (trend || []).map((s) => ({
    payMonth: s.payMonth,
    status: statusBadgeLabel(s.status),
    headcount: s.headcount || 0,
    fixedCost: s.fixedCost || 0,
    totalCost: s.totalCost || 0,
    totalCashFlow: s.totalCashFlow || 0,
    netPay: s.netPay || 0,
    tds: s.tds || 0,
  }));

  switch (cardKey) {
    case "payroll_cost":
      return {
        title: "Payroll cost",
        summary: [
          ["Payroll cost (Total Cost)", p.totalCost],
          ["Gross pay", p.grossPay],
          ["PF employer", t.pfEmployer],
          ["ESI employer", t.esiEmployer],
          ["Source", p.processed ? "Salary register" : "Not processed"],
        ],
        trendRows,
        note: "Total Cost = Gross + employer benefits (existing cost/cashflow).",
        ...commonMeta,
      };
    case "cash_outflow":
      return {
        title: "Cash outflow",
        summary: [
          ["Cash outflow", p.totalCashFlow],
          ["Bank / Net", p.netPay],
          [
            "Structured remittances (approx)",
            (Number(p.totalCashFlow) || 0) - (Number(p.netPay) || 0),
          ],
          ["Source", p.processed ? "Salary register" : "Not processed"],
        ],
        variance: analytics?.variancePie || [],
        trendRows,
        note: "Cash outflow from existing totalCashFlow on register summary.",
        ...commonMeta,
      };
    case "bank_net":
      return {
        title: "Bank / Net pay",
        summary: [
          ["Net pay", p.netPay],
          ["Gross pay", p.grossPay],
          ["Total deductions", t.totalDeductions],
          ["TDS", t.tds],
        ],
        trendRows,
        note: "Net pay from processed salary register.",
        ...commonMeta,
      };
    case "headcount":
      return {
        title: "Headcount",
        summary: [
          ["Headcount", p.headcount],
          ["Source", p.headcountSource],
          ["Listed (scope)", readiness?.total],
          ["Ready for run", readiness?.ready],
        ],
        trendRows,
        note:
          p.headcountSource === "register"
            ? "Register row count."
            : "Employee list in payroll scope.",
        ...commonMeta,
      };
    case "avg_fixed":
      return {
        title: "Average fixed cost / employee",
        summary: [
          ["Avg fixed / emp", p.avgFixed],
          ["Fixed pool", p.fixedCost],
          ["Source", p.fixedSource],
          ["Headcount used", p.headcount],
        ],
        trendRows,
        note: "Avg fixed = Σ monthly CTC / Σ headcount (employee-months).",
        ...commonMeta,
      };
    case "avg_earned":
      return {
        title: "Average earned cost / employee",
        summary: [
          ["Avg earned / emp", p.avgEarnedCost],
          ["Payroll cost", p.totalCost],
          ["Headcount", p.headcount],
        ],
        trendRows,
        note: "Avg earned = Σ totalCost / Σ headcount (employee-months).",
        ...commonMeta,
      };
    case "tax_regime":
      return {
        title: "Tax regimes",
        summary: [
          ["Old regime count", analytics?.taxRegime?.oldCount],
          ["New regime count", analytics?.taxRegime?.newCount],
          ["TDS Old", analytics?.taxRegime?.tdsOld],
          ["TDS New", analytics?.taxRegime?.tdsNew],
        ],
        note: "Regime from payroll TDS / declarations when available.",
        ...commonMeta,
      };
    case "statutory":
      return {
        title: "Statutory",
        summary: [
          ["PF employer", t.pfEmployer],
          ["ESI employer", t.esiEmployer],
          ["PF employee", t.pfEmployee],
          ["ESI employee", t.esiEmployee],
          ["Professional tax", t.professionalTax],
          ["TDS", t.tds],
          ["Statutory liabilities", t.statutoryLiabilities],
        ],
        note: "From buildSalarySummary totals on register.",
        ...commonMeta,
      };
    case "variance":
      return {
        title: "Cash outflow variance",
        summary: (analytics?.variancePie || []).map((x) => [x.label, x.value]),
        note: "Composition of cash outflow (Bank, PF, ESI, PT, TDS, Other).",
        ...commonMeta,
      };
    case "fixed_vs_earned":
      return {
        title: "Fixed vs earned",
        summary: [
          ["Fixed cost", p.fixedCost],
          ["Earned cost", p.totalCost],
          ["Gap", Math.abs((p.fixedCost || 0) - (Number(p.totalCost) || 0))],
          ["Fixed source", p.fixedSource],
        ],
        trendRows,
        note: "Gap reflects attendance/exits/variables already in register.",
        ...commonMeta,
      };
    default:
      return {
        title: cardKey || "Card detail",
        summary: [["Info", "No detail mapping"]],
        ...commonMeta,
      };
  }
}

/** Operational readiness from live employee list + structure breakdown (existing props). */
export function computeReadinessMetrics({
  employees = [],
  breakdownByEmployee = null,
  structures = [],
  insightItems = [],
}) {
  const total = employees.length;
  let withStructure = 0;
  let withCtc = 0;
  let missingStructure = 0;
  let missingCtc = 0;
  let fixedSum = 0;
  let fixedCount = 0;

  (employees || []).forEach((e) => {
    const hasCtc = Number(e.ctc) > 0;
    if (hasCtc) withCtc += 1;
    else missingCtc += 1;

    const row = breakdownByEmployee?.get?.(e.id);
    const hasStructure = !!(
      row?.structureId ||
      (row?.structureName && row.structureName !== "—")
    );
    if (hasStructure) withStructure += 1;
    else missingStructure += 1;

    const monthly =
      Number(row?.monthlyCtc) ||
      Number(row?.ctcMonthly) ||
      (Number(e.ctc) > 0 ? Number(e.ctc) / 12 : 0);
    if (monthly > 0) {
      fixedSum += monthly;
      fixedCount += 1;
    }
  });

  const insightStats = { pending: 0, approved: 0, rejected: 0 };
  (insightItems || []).forEach((row) => {
    const s = String(row?.status || "").toLowerCase();
    if (s === "approved") insightStats.approved += 1;
    else if (s === "rejected") insightStats.rejected += 1;
    else insightStats.pending += 1;
  });

  const activeTemplates = (structures || []).filter((s) => s.active !== false).length;
  const ready = Math.min(withStructure, withCtc);

  return {
    total,
    withStructure,
    withCtc,
    missingStructure,
    missingCtc,
    activeTemplates,
    ready,
    notReady: Math.max(0, total - ready),
    insightStats,
    structureFixedCost: Math.round(fixedSum),
    avgStructureFixed: fixedCount ? Math.round(fixedSum / fixedCount) : 0,
  };
}

export function parseDmy(val) {
  const s = String(val || "").trim();
  const m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (!m) return null;
  return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
}

export function dmyToYm(val) {
  const d = parseDmy(val);
  if (!d || Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
